// i18n bootstrap. Imported once at app start by src/index.js.
//
// Translation strings live under public/locales/{lang}/{namespace}.json
// and are lazy-loaded via i18next-http-backend the first time a namespace
// is touched. Keep namespaces aligned with feature areas (common, auth,
// reports, tapechart, settings, ...) so a screen only fetches what it needs.
//
// Resolution chain (i18next-browser-languagedetector → fallback):
//   1. Saved choice in localStorage  ('i18nextLng' key)
//   2. ?lng=xx URL query param        (deep links from booking engine)
//   3. navigator.language             (browser preference)
//   4. fallbackLng = 'en'
//
// IMPORTANT: the backend's LocaleMiddleware does its own resolution per
// request and ignores anything the frontend tells it via query string
// — except '?lang=' for the booking engine. For admin app calls we want
// the user's preferred_language (stored on UserProfile) to drive both
// sides, so on login we sync localStorage to user.preferred_language.
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import HttpBackend from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';

export const SUPPORTED_LANGUAGES = [
  { code: 'en',  label: 'English',    nativeLabel: 'English' },
  { code: 'lt',  label: 'Lithuanian', nativeLabel: 'Lietuvių' },
  { code: 'fil', label: 'Filipino',   nativeLabel: 'Filipino' },
  { code: 'th',  label: 'Thai',       nativeLabel: 'ไทย' },
  { code: 'es',  label: 'Spanish',    nativeLabel: 'Español' },
  { code: 'fr',  label: 'French',     nativeLabel: 'Français' },
  { code: 'de',  label: 'German',     nativeLabel: 'Deutsch' },
  { code: 'it',  label: 'Italian',    nativeLabel: 'Italiano' },
  { code: 'pt',  label: 'Portuguese', nativeLabel: 'Português' },
  { code: 'id',  label: 'Indonesian', nativeLabel: 'Bahasa Indonesia' },
];

i18n
  .use(HttpBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LANGUAGES.map((l) => l.code),
    // 'tl' is sometimes returned by browsers/OSes for Tagalog; alias to fil.
    nonExplicitSupportedLngs: false,
    load: 'languageOnly',           // 'en-US' → 'en'
    // 'ui' holds strings extracted from screens that were hard-coded in
    // English (2026-10-25); one top-level key per screen/component.
    ns: ['common', 'reports', 'operations', 'ui', 'tours'],
    defaultNS: 'common',
    backend: {
      loadPath: '/locales/{{lng}}/{{ns}}.json',
    },
    detection: {
      order: ['localStorage', 'querystring', 'navigator'],
      lookupQuerystring: 'lng',
      lookupLocalStorage: 'i18nextLng',
      caches: ['localStorage'],
    },
    interpolation: {
      escapeValue: false,           // React already escapes
    },
    react: {
      useSuspense: false,           // we render initial English then swap
    },
    returnNull: false,
  });

export default i18n;
